import { useCallback, useSyncExternalStore } from 'react';

export type Theme = 'dark' | 'light';

const KEY = 'vtgbackup-theme';
const DEFAULT: Theme = 'dark';

/**
 * Light/dark, persisted per browser.
 *
 * Defaults to DARK regardless of the operating system preference - this is a
 * NOC-style dashboard people leave open on a wall or a second monitor, and the
 * dark palette is the one the status colours were designed against. Someone who
 * wants light asks for it once and we remember.
 *
 * The theme is applied to <html> as a data attribute, so CSS does all the work
 * through variable overrides and no component needs to know which theme is on.
 * `color-scheme` is set alongside it so form controls, scrollbars and the
 * browser's own UI follow - without it you get dark inputs on a light page.
 *
 * ONE SHARED VALUE, NOT ONE PER COMPONENT.
 *
 * This file was a plain useState inside useTheme() until v0.0.148, which gave
 * every caller its OWN independent copy. Toggling updated the toggler's copy and
 * the <html> attribute, so every CSS variable flipped and the page looked
 * completely right - while any other component that had called useTheme() kept
 * the value it held at mount and never re-rendered.
 *
 * THAT BUG IS INVISIBLE UNTIL A SECOND CONSUMER EXISTS. This console had
 * exactly one - the toggle itself - so it looked proven for months. Adding the
 * Backup Beacon wordmark, which picks light or dark artwork in JS rather than in
 * CSS, produced the fault immediately: light page, dark wordmark.
 *
 * The tenant app hit the identical bug with its logo in v0.0.73 and was fixed
 * the same way. The two apps have separate copies of this file, so the fix did
 * not travel; that is worth remembering the next time either of them grows a
 * component that branches on the theme in JavaScript.
 *
 * useSyncExternalStore is the React primitive for exactly this: one value held
 * outside React, every subscriber re-rendering when it changes.
 */
function stored(): Theme | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
}

/** The single source of truth, held outside React. */
let current: Theme = stored() ?? DEFAULT;
const listeners = new Set<() => void>();

function set(next: Theme): void {
  if (next === current) return;
  current = next;
  try { localStorage.setItem(KEY, next); } catch { /* private mode, ignore */ }
  applyTheme(next);
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/** Call before React mounts, so there is no flash of the wrong theme. */
export function initTheme(): Theme {
  current = stored() ?? DEFAULT;
  applyTheme(current);
  return current;
}

export function useTheme(): [Theme, () => void] {
  const theme = useSyncExternalStore(subscribe, () => current, () => current);
  const toggle = useCallback(() => {
    set(current === 'dark' ? 'light' : 'dark');
  }, []);
  return [theme, toggle];
}
